/**
 * Hook pour les adresses Tracking.
 */
import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingAddresses,
  createTrackingAddress,
  updateTrackingAddress,
  patchTrackingAddress,
  deleteTrackingAddress,
  type TrackingAddress,
  type UpsertAddressPayload,
} from "../services/tracking/addressesService";

const ADDRESSES_KEY = "tracking-addresses";

export function useTrackingAddresses() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const {
    data: addresses,
    error,
    isLoading,
    mutate,
  } = useSWR<TrackingAddress[]>(
    token ? [ADDRESSES_KEY, token] : null,
    () => fetchTrackingAddresses(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);

  const createAddress = useCallback(
    async (payload: UpsertAddressPayload) => {
      const created = await createTrackingAddress(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const updateAddress = useCallback(
    async (id: number, payload: UpsertAddressPayload) => {
      const updated = await updateTrackingAddress(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const setDefaultAddress = useCallback(
    async (id: number) => {
      const updated = await patchTrackingAddress(token, id, { is_default: true });
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const removeAddress = useCallback(
    async (id: number) => {
      await deleteTrackingAddress(token, id);
      await mutate();
    },
    [token, mutate]
  );

  const list = Array.isArray(addresses) ? addresses : [];
  return {
    addresses: list,
    isLoading,
    error,
    refetch,
    createAddress,
    updateAddress,
    setDefaultAddress,
    removeAddress,
    accessToken: token,
  };
}

export type { TrackingAddress, UpsertAddressPayload };

